import { ref, Ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/services/api';

interface Toast {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface User {
  id: string;
  username: string;
  followersCount?: number;
  [key: string]: any;
}

export function useUserFollow(user: Ref<User | null>) {
  const router = useRouter();
  const following = ref(false);
  const followersCount = ref(0);
  const toast = ref<Toast>({
    show: false,
    message: '',
    type: 'info',
  });
  const isLoading = ref(false);

  // Initialize followers count when user data loads
  watch(
    () => user.value,
    (newUser) => {
      if (newUser) {
        followersCount.value = newUser.followersCount || 0;
        // Check if user is following this user
        checkFollowingStatus(newUser.id);
      }
    },
    { immediate: true }
  );

  // Check if the current user is following this user
  const checkFollowingStatus = async (userId: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      // User is not logged in, so they're not following
      following.value = false;
      return;
    }

    try {
      const response = await api.get(
        `/api/user-follows/users/${userId}/is-following`
      );

      if (response.data.success) {
        following.value = response.data.data.isFollowing;
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
      // In case of error, assume not following
      following.value = false;
    }
  };

  const handleToggleFollow = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      showLoginRequiredToast(
        'Morate biti prijavljeni da biste pratili korisnika.'
      );
      return;
    }

    // Prevent multiple clicks
    if (isLoading.value) return;

    // User is logged in, proceed with toggle follow
    isLoading.value = true;

    try {
      if (!user.value) return;

      if (following.value) {
        // Unfollow user
        await unfollowUser(user.value.id);
      } else {
        // Follow user
        await followUser(user.value.id);
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);

      toast.value = {
        show: true,
        message: 'Došlo je do greške. Pokušajte ponovo.',
        type: 'error',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const followUser = async (userId: string) => {
    const response = await api.post(`/api/user-follows/users/${userId}/follow`);

    if (response.data.success) {
      following.value = true;
      followersCount.value += 1;

      toast.value = {
        show: true,
        message: 'Uspešno ste počeli da pratite korisnika!',
        type: 'success',
      };
    } else {
      throw new Error('Failed to follow user');
    }
  };

  const unfollowUser = async (userId: string) => {
    const response = await api.delete(
      `/api/user-follows/users/${userId}/unfollow`
    );

    if (response.data.success) {
      following.value = false;
      followersCount.value = Math.max(0, followersCount.value - 1);

      toast.value = {
        show: true,
        message: 'Prestali ste da pratite korisnika.',
        type: 'info',
      };
    } else {
      throw new Error('Failed to unfollow user');
    }
  };

  const showLoginRequiredToast = (message: string) => {
    // Show toast notification
    toast.value = {
      show: true,
      message,
      type: 'info',
    };

    // Save current path to localStorage for redirect after login
    localStorage.setItem(
      'redirectAfterLogin',
      router.currentRoute.value.fullPath
    );

    // Redirect to login page after a short delay to see the notification
    setTimeout(() => {
      router.push('/login');
    }, 1500);
  };

  return {
    following,
    followersCount,
    toast,
    isLoading,
    handleToggleFollow,
  };
}
